import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@istari-frontend/ui";
import { KeyRound } from "lucide-react";
import { useEffect } from "react";

import { openLlmKeyPrompt, useLlmApiKey } from "~/ai-chat/data-source/llm-api-key-store";
import { useUserSelectableModels } from "~/ai-chat/data-source/use-user-models";

interface ModelPickerProps {
  selectedModelId: string | null;
  onSelect: (id: string) => void;
  disabled?: boolean;
}

export function ModelPicker({ selectedModelId, onSelect, disabled }: ModelPickerProps) {
  const modelsQuery = useUserSelectableModels();
  const { hasKey } = useLlmApiKey();
  const models = modelsQuery.data ?? [];

  // The list comes back in priority order, so the first entry is the default. Commit it as the
  // real selection once models load (rather than leaving it implicit) so the send carries a
  // concrete model_pk and a BYO-key default is prompted just like an explicit pick.
  const defaultModel = models[0] ?? null;
  useEffect(() => {
    if (selectedModelId !== null || defaultModel === null) return;
    onSelect(defaultModel.id);
    if (defaultModel.requiresUserKey && !hasKey) openLlmKeyPrompt();
  }, [selectedModelId, defaultModel, onSelect, hasKey]);

  // No admin-published models: there's nothing to pick, so hide the control.
  if (models.length === 0) return null;

  const effectiveId = selectedModelId ?? models[0].id;

  const handleChange = (value: string) => {
    onSelect(value);
    // A BYO-key model with no key held yet: prompt now so the next send isn't rejected.
    if (models.find((model) => model.id === value)?.requiresUserKey && !hasKey) {
      openLlmKeyPrompt();
    }
  };

  return (
    <Select value={effectiveId} onValueChange={handleChange} disabled={disabled}>
      <SelectTrigger size="xs" variant="ghost" aria-label="Model" className="text-foreground max-w-40 [&>svg]:hidden">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {models.map((model) => (
          <SelectItem key={model.id} value={model.id}>
            <span className="flex items-center gap-1.5">
              {model.name}
              {model.requiresUserKey ? <KeyRound className="text-muted-foreground size-3" /> : null}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
